/**
 * Responsive Utility Functions
 *
 * Helpers for responsive design and viewport management
 */

/**
 * Breakpoint values (must match breakpoints.css)
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  '2xl': 1400,
};

/**
 * Set viewport height CSS variable
 * Fixes the 100vh issue on mobile browsers
 */
export const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
};

/**
 * Initialize viewport height listeners
 * Call this once on app mount
 */
export const initViewportHeight = () => {
  setViewportHeight();

  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', setViewportHeight);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', setViewportHeight);
    window.removeEventListener('orientationchange', setViewportHeight);
  };
};

/**
 * Check if current viewport matches a breakpoint
 * @param {string} breakpoint - Breakpoint key (xs, sm, md, lg, xl, 2xl)
 * @returns {boolean} True if viewport width >= breakpoint
 */
export const isBreakpoint = (breakpoint) => {
  if (typeof window === 'undefined') return false;
  const width = BREAKPOINTS[breakpoint];
  return window.innerWidth >= width;
};

/**
 * Check if viewport is mobile (< md)
 * @returns {boolean}
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < BREAKPOINTS.md;
};

/**
 * Check if viewport is tablet (md to lg)
 * @returns {boolean}
 */
export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS.md && window.innerWidth < BREAKPOINTS.lg;
};

/**
 * Check if viewport is desktop (>= lg)
 * @returns {boolean}
 */
export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS.lg;
};

/**
 * Get current breakpoint name
 * @returns {string} Current breakpoint (xs, sm, md, lg, xl, 2xl)
 */
export const getCurrentBreakpoint = () => {
  if (typeof window === 'undefined') return 'xs';

  const width = window.innerWidth;

  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Create media query string for a breakpoint
 * @param {string} breakpoint - Breakpoint key
 * @param {string} type - 'min' or 'max'
 * @returns {string} Media query string
 */
export const getMediaQuery = (breakpoint, type = 'min') => {
  const width = BREAKPOINTS[breakpoint];
  if (type === 'max') {
    return `(max-width: ${width - 1}px)`;
  }
  return `(min-width: ${width}px)`;
};

/**
 * Lock body scroll (useful for modals)
 */
export const lockBodyScroll = () => {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';
};

/**
 * Unlock body scroll
 */
export const unlockBodyScroll = () => {
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.body.style.overflow = '';
  window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

/**
 * Check if device supports touch
 * @returns {boolean}
 */
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Check if device prefers reduced motion
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
